import React from 'react'
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableFooter, TableHead, TableRow, Typography } from '@mui/material'
import { blue, grey } from '@mui/material/colors'
import { useGlobalState } from 'state'
import { ObservatoryValue, ObservatoryRow, ObservatoryType } from 'common/types'
import { StageAlertLevel, StageForecastHours } from 'common/consts'
import { isPrivateMode, isYosokuMode} from 'common/config'
import { StyledTableCell } from 'components/ui/StyledTableCell'
import { StyledTableRow } from 'components/ui/StyledTableRow'
import { StageChart } from './StageChart'
import dayjs from 'dayjs'

const AlertLevelRow = ({label, color, level}: {
  label: string
  color: string
  level?: number
}) => {
  if (level === undefined) return <></>
  const getLabel = (): string => {
    const text = label.replace('（', '(').replace('）', ')')
    if (text.includes('(')) {
      return text.split('(')[0] + '\n(' + text.split('(')[1]
    } else {
      return text
    }
  }
  return (
    <Stack direction="row" alignItems={'center'} sx={{pl: 3}}>
      <Box width={50}>
        <Box width={20} height={3} sx={{backgroundColor: color}}></Box>
      </Box>
      <Typography variant='caption' width={120} sx={{whiteSpace: 'pre'}}>{getLabel()}</Typography>
      <Typography variant='caption' width={80} textAlign={'right'}>{level} m</Typography>
    </Stack>
  )
}

export const StageDetail = () => {
  const state = useGlobalState()
  const [stage, setStage] = React.useState<ObservatoryRow | null>(null)
  const [timeSeries, setTimeSeries] = React.useState<ObservatoryValue[]>([])

  React.useEffect(() => {
    setStage(state.observatory.selected)
  }, [state.observatory.selected])

  const filterTimeSeries = () => {
    let hours = 0
    if (isPrivateMode) {
      hours = StageForecastHours
    } else if (isYosokuMode) {
      hours = 1
    }
    const limitDate = state.app.baseDate.add(hours, 'hours').format('YYYY/MM/DD HH:mm')
    let targets = state.observatory.timeSeries
      .filter(ts => ts.date <= limitDate)
    if (isYosokuMode) {
      const gteDate = state.app.baseDate.subtract(35, 'minutes').format('YYYY/MM/DD HH:mm')
      targets = targets.filter(ts => ts.date >= gteDate)
    }
    setTimeSeries(targets
      .map(ts => {
        const jst = dayjs.utc(ts.date).local().format('YYYY/MM/DD HH:mm')
        return {...ts, date: jst}
      }))
  }

  React.useEffect(() => {
    filterTimeSeries()
  }, [state.observatory.timeSeries])

  const isAlertLevel = (): boolean => {
    if (!stage || stage.kiki === true) return false
    return stage.outbreakLevel !== undefined ||
      stage.floodLevel !== undefined ||
      stage.dangerousLevel !== undefined ||
      stage.evacuationLevel !== undefined ||
      stage.warningLevel !== undefined ||
      stage.standbyLevel !== undefined
  }

  const isKikiAlertLevel = (): boolean => {
    if (!stage || stage.kiki !== true) return false
    return stage.startLevel !== undefined || stage.outbreakLevel !== undefined
  }

  const isTideAlertLevel = (): boolean => {
    if (!stage || stage.type !== ObservatoryType.Tide) return false
    return stage.maxExperiencedLevel !== undefined && stage.maxExperiencedLevelName !== undefined
  }

  const getTimeSeriesColor = (row: ObservatoryValue) => {
    return row.type === 'forecast' ? blue[500] : grey[900]
  }

  return ( stage &&
    <Box>

      { (stage && (timeSeries.length > 0 || stage.level !== undefined)) && (
        <Stack direction="column">
          <Typography variant='caption'>（m）</Typography>
          <StageChart stage={stage} height={220} timeSeries={timeSeries} />
        </Stack>
      )}

      { isAlertLevel() && (
        <Stack direction="column" color={grey[900]} mb={1}>
          <Typography variant='caption'>基準水位</Typography>
          <AlertLevelRow color={StageAlertLevel.OUTBREAK.color} label={StageAlertLevel.OUTBREAK.label} level={stage.outbreakLevel} />
          <AlertLevelRow color={StageAlertLevel.FLOOD.color} label={StageAlertLevel.FLOOD.label} level={stage.floodLevel} />
          <AlertLevelRow color={StageAlertLevel.DANGEROUS.color} label={StageAlertLevel.DANGEROUS.label} level={stage.dangerousLevel} />
          <AlertLevelRow color={StageAlertLevel.EVACUATION.color} label={StageAlertLevel.EVACUATION.label} level={stage.evacuationLevel} />
          <AlertLevelRow color={StageAlertLevel.WARNING.color} label={StageAlertLevel.WARNING.label} level={stage.warningLevel} />
          <AlertLevelRow color={StageAlertLevel.STANDBY.color} label={StageAlertLevel.STANDBY.label} level={stage.standbyLevel} />
        </Stack>
      )}

      { isKikiAlertLevel() && (
        <Stack direction="column" color={grey[900]} mb={1}>
          <Typography variant='caption'>基準水位</Typography>
          <AlertLevelRow color={StageAlertLevel.FLOOD.color} label={StageAlertLevel.FLOOD.label} level={stage.floodLevel} />
          <AlertLevelRow color={StageAlertLevel.WARN.color} label={StageAlertLevel.WARN.label} level={stage.warnLevel} />
          <AlertLevelRow color={StageAlertLevel.START.color} label={StageAlertLevel.START.label} level={stage.startLevel} />
        </Stack>
      )}

      { isTideAlertLevel() && (
        <Stack direction="column" color={grey[900]} mb={1}>
          <Typography variant='caption'>基準水位</Typography>
          <AlertLevelRow
            label={stage.maxExperiencedLevelName ? stage.maxExperiencedLevelName : '過去最高潮位'}
            color={StageAlertLevel.TIDEMAX.color}
            level={stage.maxExperiencedLevel} />
        </Stack>
      )}

      <TableContainer component={Paper}>
        <Table size="small" aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell width={180}>日時</StyledTableCell>
              <StyledTableCell width={130}>水位（m）</StyledTableCell>
              <StyledTableCell></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeSeries.map((ts, i) => (
              <StyledTableRow key={i}>
                <StyledTableCell component="th" scope="row" color={getTimeSeriesColor(ts)}>
                  <Typography variant='body2'>{ts.date}</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2' textAlign={'right'} color={getTimeSeriesColor(ts)}>
                    {ts.level === undefined ? '-' : ts.level.toFixed(2)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell></StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
          {
            timeSeries.length === 0 && (
              <TableFooter>
                <TableRow>
                  <StyledTableCell colSpan={3}>データがありません</StyledTableCell>
                </TableRow>
              </TableFooter>
            )
          }
        </Table>
      </TableContainer>

    </Box>
  )
}
